import { HtmlService } from "../../../core/services/html.service";
import { DateParserService } from "../../../core/services/date-parser.service";
import { DOT, STYLES, MESSAGE_WIDTH_MODE } from "../../constants";
import { renderMessage } from "../../templates";
import type { IChatOptions, IMessage, IAuthor, IChatMessages, IMessageSettings, MessageStatusTemplateFunction } from "../../models";
import type { DataManager } from "../data-manager.collaborator";
import type { IKendoJQuery } from "../../../core";

declare const $: any;

export interface IMessageRendererContext {
    list: IKendoJQuery;
    options: IChatOptions;
    dataManager: DataManager;
    htmlService: HtmlService;
    dateParserService: DateParserService;
    dataItem: (message: IKendoJQuery) => IMessage | null;
    getReplyMessage: (message: IMessage) => IMessage | null;
    initMessageToolbar: (element: IKendoJQuery) => void;
    initFileMenus: (element: IKendoJQuery) => void;
}

export class MessageRenderer {
    private context: IMessageRendererContext;

    constructor(context: IMessageRendererContext) {
        this.context = context;
    }

    renderMessage(message: IMessage): void {
        const options = this.context.options;
        const componentMessages = options.messages;
        const expandable = options.allowMessageCollapse;
        const fullWidth = options.messageWidthMode === MESSAGE_WIDTH_MODE.FULL;
        const author: IAuthor = {
            id: message.authorId,
            name: message.authorName,
            imageUrl: message.authorImageUrl,
            imageAltText: message.authorImageAltText
        };
        const isOwnMessage = author.id === options.authorId;
        const replyMessage = this.context.getReplyMessage(message);
        const messageTimeFormat = options.messageTimeFormat;
        const skipSanitization = options.skipSanitization;
        const statusTemplate = options.messageStatusTemplate;
        const userSettings = isOwnMessage ? options.authorMessageSettings : options.receiverMessageSettings;
        const messageSettings = this._mergeMessageSettings(options, userSettings);

        if (this._handleExistingMessageUpdate({
            message, author, isOwnMessage, replyMessage, componentMessages, expandable, messageTimeFormat, skipSanitization, statusTemplate, messageSettings
        })) {
            return;
        }

        const targetGroupElement = this._findTargetMessageGroup();
        const canGroup = this._canGroupWithLastMessage(targetGroupElement, author.id);

        if (canGroup && targetGroupElement.length) {
            this._addMessageToExistingGroup({
                message, author, isOwnMessage, replyMessage, componentMessages, expandable, targetGroupElement, messageTimeFormat, skipSanitization, statusTemplate, messageSettings
            });
        } else {
            const showTimestamp = this._shouldShowTimestamp(message);
            this._createNewMessageGroup({
                message, author, isOwnMessage, replyMessage, componentMessages, expandable, fullWidth, messageTimeFormat, showTimestamp, skipSanitization, statusTemplate, messageSettings
            });
        }
    }

    _mergeMessageSettings(options: IChatOptions, userSettings: IMessageSettings | null | undefined): IMessageSettings {
        const merged: IMessageSettings = {
            showAvatar: options.showAvatar,
            showUsername: options.showUsername,
            showTimestamp: options.timestampVisibility !== "never",
            allowMessageCollapse: options.allowMessageCollapse,
            messageWidthMode: options.messageWidthMode
        };

        if (userSettings) {
            if (userSettings.showAvatar !== undefined) merged.showAvatar = userSettings.showAvatar;
            if (userSettings.showUsername !== undefined) merged.showUsername = userSettings.showUsername;
            if (userSettings.showTimestamp !== undefined) merged.showTimestamp = userSettings.showTimestamp;
            if (userSettings.allowMessageCollapse !== undefined) merged.allowMessageCollapse = userSettings.allowMessageCollapse;
            if (userSettings.messageWidthMode !== undefined) merged.messageWidthMode = userSettings.messageWidthMode;
            if (userSettings.enableFileActions !== undefined) merged.enableFileActions = userSettings.enableFileActions;
            if (userSettings.enableContextMenuActions !== undefined) merged.enableContextMenuActions = userSettings.enableContextMenuActions;
            if (userSettings.messageToolbarActions !== undefined) merged.messageToolbarActions = userSettings.messageToolbarActions;
            if (userSettings.messageActions !== undefined) merged.messageActions = userSettings.messageActions;
        }

        return merged;
    }

    _shouldShowTimestamp(message: IMessage): boolean {
        if (!message.timestamp) {
            return false;
        }

        const lastNonTypingMessage = this.context.list.find(DOT + STYLES.message).filter(function() {
            return $(this).find(DOT + STYLES.typingIndicator).length === 0;
        }).last() as IKendoJQuery;

        if (!lastNonTypingMessage.length) {
            return true;
        }

        const lastMessageData = this.context.dataItem(lastNonTypingMessage);
        if (!lastMessageData || !lastMessageData.timestamp) {
            return true;
        }

        const currentMessageDate = this.context.dateParserService.parseDate(message.timestamp);
        const lastMessageDate = this.context.dateParserService.parseDate(lastMessageData.timestamp);

        if (!currentMessageDate || !lastMessageDate) {
            return false;
        }

        const currentDay = new Date(currentMessageDate);
        currentDay.setHours(0, 0, 0, 0);

        const lastDay = new Date(lastMessageDate);
        lastDay.setHours(0, 0, 0, 0);

        return currentDay.getTime() !== lastDay.getTime();
    }

    _handleExistingMessageUpdate(context: {
        message: IMessage;
        author: IAuthor;
        isOwnMessage: boolean;
        replyMessage: IMessage | null;
        componentMessages: IChatMessages;
        expandable: boolean;
        messageTimeFormat: string;
        skipSanitization: boolean;
        statusTemplate?: MessageStatusTemplateFunction | null;
        messageSettings?: IMessageSettings;
    }): boolean {
        const { message, author, isOwnMessage, replyMessage, componentMessages, expandable, messageTimeFormat, skipSanitization, statusTemplate, messageSettings } = context;
        const options = this.context.options;
        const existingMessageElement = this.context.list.find(DOT + STYLES.message + `[data-uid="${this.context.htmlService.encode(message.uid ?? "")}"]`);

        if (existingMessageElement.length) {
            const messageTemplate = options.messageTemplate;

            let messageHtml: string;
            if (messageTemplate) {
                messageHtml = messageTemplate(
                    { ...message, isOwnMessage, author },
                    replyMessage,
                    true,
                    componentMessages,
                    messageSettings?.allowMessageCollapse ?? expandable,
                    messageTimeFormat,
                    skipSanitization,
                    statusTemplate
                );
            } else {
                messageHtml = renderMessage(
                    { ...message, isOwnMessage, author },
                    replyMessage,
                    true,
                    componentMessages,
                    messageSettings?.allowMessageCollapse ?? expandable,
                    messageTimeFormat,
                    skipSanitization,
                    statusTemplate,
                    options.filesLayoutMode,
                    this.context.htmlService
                );
            }

            const updatedMessageElement = $(messageHtml);
            existingMessageElement.replaceWith(updatedMessageElement);
            this.context.initMessageToolbar(updatedMessageElement.find(DOT + STYLES.messageToolbar) as IKendoJQuery);
            this.context.initFileMenus(updatedMessageElement);
            return true;
        }
        return false;
    }

    _findTargetMessageGroup(): IKendoJQuery {
        let lastGroupElement = this.context.list.children(DOT + STYLES.messageGroup).last();
        let targetGroupElement = lastGroupElement;

        const messagesInLastGroup = lastGroupElement.find(DOT + STYLES.message);
        if (messagesInLastGroup.length === 1) {
            const onlyMessage = messagesInLastGroup.first();
            const isTypingIndicator = onlyMessage.find(DOT + STYLES.typingIndicator).length > 0;

            if (isTypingIndicator) {
                targetGroupElement = lastGroupElement.prev(DOT + STYLES.messageGroup);
            }
        }

        return targetGroupElement as IKendoJQuery;
    }

    _canGroupWithLastMessage(targetGroupElement: IKendoJQuery, authorId: string): boolean {
        const lastMessageInTargetGroup = targetGroupElement.find(DOT + STYLES.message).filter(function() {
            return $(this).find(DOT + STYLES.typingIndicator).length === 0;
        }).last() as IKendoJQuery;

        const lastMessageData = this.context.dataItem(lastMessageInTargetGroup);
        return lastMessageData?.authorId === authorId;
    }

    _addMessageToExistingGroup(context: {
        message: IMessage;
        author: IAuthor;
        isOwnMessage: boolean;
        replyMessage: IMessage | null;
        componentMessages: IChatMessages;
        expandable: boolean;
        targetGroupElement: IKendoJQuery;
        messageTimeFormat: string;
        skipSanitization: boolean;
        statusTemplate?: MessageStatusTemplateFunction | null;
        messageSettings?: IMessageSettings;
    }): void {
        const { message, author, isOwnMessage, replyMessage, componentMessages, expandable, targetGroupElement, messageTimeFormat, skipSanitization, statusTemplate, messageSettings } = context;
        const options = this.context.options;
        const messageTemplate = options.messageTemplate;
        const messageGroupContent = targetGroupElement.find(DOT + STYLES.messageGroupContent);

        let messageHtml: string;
        if (messageTemplate) {
            messageHtml = messageTemplate(
                { ...message, isOwnMessage, author },
                replyMessage,
                true,
                componentMessages,
                messageSettings?.allowMessageCollapse ?? expandable,
                messageTimeFormat,
                skipSanitization,
                statusTemplate
            );
        } else {
            messageHtml = renderMessage(
                { ...message, isOwnMessage, author },
                replyMessage,
                true,
                componentMessages,
                messageSettings?.allowMessageCollapse ?? expandable,
                messageTimeFormat,
                skipSanitization,
                statusTemplate,
                options.filesLayoutMode,
                this.context.htmlService
            );
        }

        const messageElement = $(messageHtml);
        messageGroupContent.append(messageElement);
        this.context.initMessageToolbar(messageElement.find(DOT + STYLES.messageToolbar) as IKendoJQuery);
        this.context.initFileMenus(messageElement);
        this._moveTypingIndicatorsToEnd();
    }

    _createNewMessageGroup(context: {
        message: IMessage;
        author: IAuthor;
        isOwnMessage: boolean;
        replyMessage: IMessage | null;
        componentMessages: IChatMessages;
        expandable: boolean;
        fullWidth: boolean;
        messageTimeFormat: string;
        showTimestamp: boolean;
        skipSanitization: boolean;
        statusTemplate?: MessageStatusTemplateFunction | null;
        messageSettings?: IMessageSettings;
    }): IKendoJQuery {
        const { message, author, isOwnMessage, replyMessage, componentMessages, expandable, fullWidth, messageTimeFormat, showTimestamp, skipSanitization, statusTemplate, messageSettings } = context;
        const options = this.context.options;
        const messageGroupTemplate = options.messageGroupTemplate;
        const timestampTemplate = options.timestampTemplate;
        const messageTemplate = options.messageTemplate;

        const groupElement = $(messageGroupTemplate({
            message,
            author,
            isOwnMessage,
            replyMessage,
            downloadAll: true,
            messages: componentMessages,
            expandable,
            fullWidth,
            messageTimeFormat,
            timestampTemplate,
            statusTemplate,
            showTimestamp,
            messageTemplate,
            skipSanitization,
            messageSettings,
            filesLayoutMode: options.filesLayoutMode,
            contentTemplate: options.messageContentTemplate,
            authorMessageContentTemplate: options.authorMessageContentTemplate,
            receiverMessageContentTemplate: options.receiverMessageContentTemplate,
            attachmentTemplate: options.attachmentTemplate,
            attachmentLayout: options.attachmentLayout,
            userStatusTemplate: options.userStatusTemplate
        }));

        this.context.list.append(groupElement);
        this.context.initMessageToolbar(groupElement.find(DOT + STYLES.messageToolbar) as IKendoJQuery);
        this.context.initFileMenus(groupElement);
        this._moveTypingIndicatorsToEnd();

        return groupElement;
    }

    _moveTypingIndicatorsToEnd(): void {
        const ctx = this.context;
        const options = ctx.options;
        const messageGroupTemplate = options.messageGroupTemplate;
        const fullWidth = options.messageWidthMode === MESSAGE_WIDTH_MODE.FULL;

        const typingMessages = ctx.list.find(DOT + STYLES.message).filter(function() {
            return $(this).find(DOT + STYLES.typingIndicator).length > 0;
        });

        typingMessages.each(function() {
            const typingMessage = $(this);
            const messageGroup = typingMessage.closest(DOT + STYLES.messageGroup);
            const messagesInGroup = messageGroup.find(DOT + STYLES.message);

            if (messagesInGroup.length === 1) {
                messageGroup.detach().appendTo(ctx.list);
            } else {
                typingMessage.remove();

                const message = ctx.dataItem(typingMessage);
                if (!message) return;

                const author: IAuthor = {
                    id: message.authorId,
                    name: message.authorName,
                    imageUrl: message.authorImageUrl,
                    imageAltText: message.authorImageAltText
                };

                const typingGroupElement = $(messageGroupTemplate({
                    message,
                    author,
                    isOwnMessage: author.id === options.authorId,
                    fullWidth,
                    messageTimeFormat: options.messageTimeFormat,
                    showTimestamp: false,
                    timestampTemplate: options.timestampTemplate,
                    messageTemplate: options.messageTemplate,
                    filesLayoutMode: options.filesLayoutMode
                }));

                ctx.list.append(typingGroupElement);
            }
        });
    }
}
